import { useAudio } from '@providers'
import classNames from 'classnames'
import { JSX } from 'react'
import { useTranslation } from 'react-i18next'

import { TooltipContent, useIsMobile } from '@ds'
import { webApp } from '@shared'
import {
  useInventory,
  useInventoryActions,
  useOverlay,
  useUser,
  useUserBankTaps,
} from '@store'

import styles from './InventoryButton.module.scss'

export const InventoryButton = (): JSX.Element => {
  const isMobile = useIsMobile()
  const isDevelopment = import.meta.env.MODE === 'development'
  const isOpen = useInventory.store.isOpen()
  const { toggle } = useInventoryActions()
  const { playClose, playOpen } = useAudio()

  const tooltipVisible = useInventory.store.tooltipVisible()
  const { profile } = useUser()
  const overlay = useOverlay.store.overlay()
  const taps = useUserBankTaps()
  const { t } = useTranslation()

  const shouldShowTooltipInPrologue =
    profile?.battlepassLevel === 1 &&
    overlay === null &&
    taps?.balance === 0 &&
    !profile?.passedPrologue

  return (
    <div
      className={classNames(
        styles.inventoryButton,
        isOpen && styles.isActive,
        (isMobile || isDevelopment) && styles.isMobile
      )}
      onClick={() => {
        void (isOpen ? playClose() : playOpen())
        webApp.HapticFeedback.impactOccurred('soft')
        toggle()
      }}
    >
      <div className={styles.inner}>
        <svg className={styles.icon} viewBox="0 0 32 32" fill="none">
          <path
            d="M18.1441 10.4679L13.1585 7.67267L3.43604 13.33L3.68058 24.5775L8.66613 27.3727"
            stroke="white"
            strokeWidth="1.33333"
            strokeMiterlimit="10"
          />
          <path
            d="M18.1441 10.4679L13.1585 7.67267L3.43604 13.33L3.68058 24.5775L8.66613 27.3727"
            stroke="url(#paint0_radial_3388_4407)"
            strokeWidth="1.33333"
            strokeMiterlimit="10"
          />
          <path
            d="M9.1494 18.1066L9.22144 23.8218L18.982 29.4131L28.6004 23.5775L28.5283 17.8623"
            stroke="white"
            strokeWidth="1.33333"
            strokeMiterlimit="10"
          />
          <path
            d="M9.1494 18.1066L9.22144 23.8218L18.982 29.4131L28.6004 23.5775L28.5283 17.8623"
            stroke="url(#paint1_radial_3388_4407)"
            strokeWidth="1.33333"
            strokeMiterlimit="10"
          />
          <path
            d="M20.3767 21.9489L25.2902 19.0289L25.2521 7.78032L15.3892 2.36835L10.4757 5.28835"
            stroke="white"
            strokeWidth="1.33333"
            strokeMiterlimit="10"
          />
          <path
            d="M20.3767 21.9489L25.2902 19.0289L25.2521 7.78032L15.3892 2.36835L10.4757 5.28835"
            stroke="url(#paint2_radial_3388_4407)"
            strokeWidth="1.33333"
            strokeMiterlimit="10"
          />
          <defs>
            <radialGradient
              id="paint0_radial_3388_4407"
              cx="0"
              cy="0"
              r="1"
              gradientUnits="userSpaceOnUse"
              gradientTransform="translate(18.1441 10.4679) rotate(152.968) scale(23.2924 15.3829)"
            >
              <stop />
              <stop offset="1" stopColor="white" />
            </radialGradient>
            <radialGradient
              id="paint1_radial_3388_4407"
              cx="0"
              cy="0"
              r="1"
              gradientUnits="userSpaceOnUse"
              gradientTransform="translate(9.1494 18.1066) rotate(32.9679) scale(23.2924 15.3829)"
            >
              <stop />
              <stop offset="1" stopColor="white" />
            </radialGradient>
            <radialGradient
              id="paint2_radial_3388_4407"
              cx="0"
              cy="0"
              r="1"
              gradientUnits="userSpaceOnUse"
              gradientTransform="translate(20.3767 21.9489) rotate(-87.0321) scale(23.2924 15.3829)"
            >
              <stop />
              <stop offset="1" stopColor="white" />
            </radialGradient>
          </defs>
        </svg>
      </div>
      {tooltipVisible && (
        <TooltipContent x="left" y="top" className={styles.tooltip}>
          {t('shared.inventory.tooltip')}
        </TooltipContent>
      )}
      {shouldShowTooltipInPrologue && (
        <TooltipContent x="left" y="top" className={styles.tooltip}>
          {t('prologue.inventoryTooltip')}
        </TooltipContent>
      )}
    </div>
  )
}
